/**
 * Ribbon > Analysis buttons → what they open (pure; used by pages/MapView.js).
 *
 * resolveRibbonAnalysis(id) returns one of
 *   { action: 'panel', tool, params }  open the Analysis panel on `tool` (components/AnalysisPanel.js TOOLS id)
 *   { action: 'list' }                 open the Analysis panel on its tool list (All Tools)
 *   { action: 'nearest' }              Map > Find nearest (setTool('nearest'))
 *   { action: 'heatmap' }              the Map > Heatmap toggle (heatmap style on point layers)
 */
const PANEL_TOOLS = {
  buffer: { tool: 'buffer' },
  centroid: { tool: 'centroid' },
  centroids: { tool: 'centroid' },
  dissolve: { tool: 'dissolve' },
  convex: { tool: 'convex' },
  clip: { tool: 'clip' },
  merge: { tool: 'merge' },
  union: { tool: 'union' },
  intersect: { tool: 'intersect' },
  kriging: { tool: 'gridding', params: { method: 'kriging' } },
  idw: { tool: 'gridding', params: { method: 'idw' } },
  hotspot: { tool: 'lisa', params: { hotspotMethod: 'lisa' } },
  getis: { tool: 'lisa', params: { hotspotMethod: 'getis' } },
};

export function resolveRibbonAnalysis(id) {
  if (id === 'nearest') return { action: 'nearest' };
  if (id === 'heatmap') return { action: 'heatmap' };
  const entry = PANEL_TOOLS[id];
  if (!entry) return { action: 'list' };
  return { action: 'panel', tool: entry.tool, params: { ...(entry.params || {}) } };
}

/** Current map viewport as [w, s, e, n] (null before the map exists). `mapRefHolder` is MapView's. */
export function viewExtentOf(mapRefHolder) {
  try {
    const map = mapRefHolder?.current?.current?.getMap?.();
    const b = map?.getBounds?.();
    if (!b) return null;
    const ext = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    return ext.every(Number.isFinite) ? ext : null;
  } catch {
    return null;
  }
}

// ── Heatmap toggle (Map > Heatmap and Analysis > Heatmap) ─────────────────────

const POINT_TYPES = ['Point', 'MultiPoint'];

/** A layer counts as a point layer when most of its (first 200) features are points. */
export function isPointLayer(geojson) {
  const feats = (geojson?.features || []).slice(0, 200).filter((f) => f?.geometry);
  if (!feats.length) return false;
  const pts = feats.filter((f) => POINT_TYPES.includes(f.geometry.type)).length;
  return pts / feats.length >= 0.5;
}

/**
 * Layers the heatmap toggle acts on: the active layer when it is a point layer, otherwise
 * every visible point layer. `layers` = [{ id, geojson, visible }] keyed by style id.
 */
export function heatmapTargets(layers, activeId) {
  const active = activeId ? layers.find((l) => l.id === activeId) : null;
  if (active && isPointLayer(active.geojson)) return [active.id];
  return layers.filter((l) => l.visible !== false && isPointLayer(l.geojson)).map((l) => l.id);
}

/** True when any of `ids` currently renders with the heatmap style. */
export function heatmapActive(layerStyles, ids) {
  return ids.some((id) => layerStyles?.[id]?.mode === 'heatmap');
}

/**
 * Style updates for one toggle. Turning on keeps the previous style (restored on the way
 * back); a layer without a previous style returns to its default (null = remove override).
 * @returns {{ on: boolean, updates: Array<[string, object|null]> }}
 */
export function toggleHeatmapStyles(layerStyles, ids, defaultStyle) {
  const styles = layerStyles || {};
  if (heatmapActive(styles, ids)) {
    const updates = ids.filter((id) => styles[id]?.mode === 'heatmap').map((id) => {
      const { _beforeHeatmap, ...rest } = styles[id];
      if (_beforeHeatmap === undefined || _beforeHeatmap === null) return [id, null];
      return [id, { ...rest, ..._beforeHeatmap }];
    });
    return { on: false, updates };
  }
  const updates = ids.map((id) => {
    const prev = styles[id];
    return [id, { ...(prev || defaultStyle), mode: 'heatmap', _beforeHeatmap: prev ? { mode: prev.mode } : null }];
  });
  return { on: true, updates };
}
